import { z } from "zod";
import { taxKindOf } from "./tax.js";

// ---------------------------------------------------------------------------
// パートナー手数料（紹介手数料）の1回目（#213、docs/40 §2〜§7。2026-10-19 射場さんの決定 ★A〜E）
//
//   パートナー（管理会社など）が工事を紹介 → 見積に手数料を上乗せ → 工事代金の入金後にパートナーへ払う（払うのは2回目）。
//   ・会社の門＝モジュール referral（設定 → 見積・請求 で ON/OFF）。プランの門＝planAllows(plan, "referralFee")（1回目は全プラン）
//   ・顧客の詳細でパートナー ON・手数料 ％（小数2桁・0 より大きく 90 未満）・備考 20字
//   ・見積の「詳細」でパートナーを付ける（見積ごとに率を変えられる＝★A）。行の率は NULL＝見積の率に従う・0＝対象外
//   ・行の詳細の下段：率で／円で・粗利率・パートナー・上乗せ。S ＝ C ÷ (1 − m − f)、p ＝ (m ＋ f) ÷ (1 − m − f)、m ＝ 1 − f − C ÷ S
//     単価 ＝ S ÷ 数量 を会社の単位（1／10／100円）で切り上げ（決定4）
//   ・手数料（行）＝ 金額 × f を1円に四捨五入（0 から遠い方。値引きは負）。合計＝行の和（DB の fn_referral_fee と同じ式）
//   ・お客様向け（紙・PDF・送信・比較・見積依頼）には出さない
//
// 計算はここの1か所（画面の行の詳細・表・合計の帯・取込の1枚、API の GET /estimates/:id）。
// 発行の瞬間に固める値は DB の fn_referral_fees_freeze（同じ式）。fn_recalc_revision は変えない
// ---------------------------------------------------------------------------

const Yen = z.number().int().min(-999_999_999_999).max(999_999_999_999);

/** 上乗せ後の単価の丸め（tenants.markup_unit_price_round）。いつも切り上げ（決定4） */
export const MARKUP_ROUNDS = [1, 10, 100] as const;
export const MarkupRound = z.union([z.literal(1), z.literal(10), z.literal(100)]);
export type MarkupRound = z.infer<typeof MarkupRound>;

/** パートナーの率の上限（％、これ未満） */
export const REFERRAL_PCT_MAX = 90;
/** パートナーの率（％、小数2桁）。0 以上 90 未満。0＝対象外（行）・既定（顧客の登録） */
export const ReferralPct = z.number().min(0).lt(REFERRAL_PCT_MAX).multipleOf(0.01);
export type ReferralPct = z.infer<typeof ReferralPct>;
/** 備考（社内用）の上限 */
export const REFERRAL_MEMO_MAX = 20;

/** 手数料の対象の行の種類：ふつうの行・率の行・値引き（見出し・文章・別途・小計は無し。docs/40 §5.2） */
export const REFERRAL_ROLES = ["detail", "overhead_rate", "discount"] as const;

/** ％（小数2桁）→ 1万分率の整数 */
const bpOf = (pct: number): number => Math.round(pct * 100);
/** 数量の100倍（数量は小数2桁）。空・0 以下は 1 */
const qty100Of = (q: number | null | undefined): number => (q != null && q > 0 ? Math.round(q * 100) : 100);
/** 割り算の切り上げ（d > 0） */
function ceilDiv(n: bigint, d: bigint): bigint {
  const q = n / d;
  return n % d !== 0n && n > 0n ? q + 1n : q;
}
const roundOf = (r: number | null | undefined): bigint => BigInt(r && r > 0 ? Math.round(r) : 1);

/** 小数2桁に（％の見せ方） */
export const pct2 = (v: number | null): number | null => (v == null || !Number.isFinite(v) ? null : Math.round(v * 100) / 100);

/**
 * 単価（上乗せ後）：仕入れ（行の合計）÷ (1 − 率) ÷ 数量 を round 円で切り上げ。率＝粗利率＋パートナー（％）。
 * 率が 0 未満・100 以上なら null。仕入れの単価で割る DB の fn_markup_unit_price と同じ考え方（数量 1 なら同じ値）
 */
export function unitPriceFromRate(cost: number, quantity: number | null | undefined, ratePct: number, round: number = 1): number | null {
  const r = bpOf(ratePct);
  if (!Number.isFinite(ratePct) || r < 0 || r >= 10000) return null;
  const rd = roundOf(round);
  return Number(ceilDiv(BigInt(Math.round(cost)) * 1_000_000n, BigInt(10000 - r) * BigInt(qty100Of(quantity)) * rd) * rd);
}

/** 単価（上乗せ後）：仕入れ × (1 ＋ 上乗せ％) ÷ 数量 を round 円で切り上げ（「率で」で上乗せを打った） */
export function unitPriceFromMarkup(cost: number, quantity: number | null | undefined, markupPct: number, round: number = 1): number | null {
  const p = bpOf(markupPct);
  if (!Number.isFinite(markupPct) || p < 0) return null;
  const rd = roundOf(round);
  return Number(ceilDiv(BigInt(Math.round(cost)) * BigInt(10000 + p) * 100n, 10000n * BigInt(qty100Of(quantity)) * rd) * rd);
}

/** 単価（上乗せ後）：(仕入れ ＋ 円) ÷ 数量 を round 円で切り上げ（「円で」） */
export function unitPriceFromYen(cost: number, quantity: number | null | undefined, markupYen: number, round: number = 1): number | null {
  const rd = roundOf(round);
  return Number(ceilDiv(BigInt(Math.round(cost + markupYen)) * 100n, BigInt(qty100Of(quantity)) * rd) * rd);
}

/** {vendorQuote}の取込の単価：仕入れの単価 ÷ (1 − 率) を round 円で切り上げ（DB の fn_markup_unit_price と同じ） */
export function markupUnitPrice(costUnit: number, ratePct: number, round: number = 1): number | null {
  return unitPriceFromRate(costUnit, 1, ratePct, round);
}

/** 上乗せ p（仕入れに対する％）＝ (m ＋ f) ÷ (1 − m − f)。m＋f が 100 以上なら null */
export function markupOfRates(marginPct: number, referralPct: number = 0): number | null {
  const t = marginPct + referralPct;
  if (!Number.isFinite(t) || t >= 100 || t < 0) return null;
  return (t / (100 - t)) * 100;
}
/** 粗利率 m（売価に対する％）＝ 1 − f − C ÷ S。売価が 0 以下なら null（手数料後の利益率と同じ） */
export function marginOfPrice(cost: number, sale: number, referralPct: number = 0): number | null {
  if (!(sale > 0)) return null;
  return 100 - referralPct - (cost / sale) * 100;
}
/** 上乗せ p ＝ S ÷ C − 1（％）。仕入れが 0 以下なら null */
export function markupOfPrice(cost: number, sale: number): number | null {
  if (!(cost > 0)) return null;
  return (sale / cost - 1) * 100;
}

/** 手数料 ＝ 金額 × 率（％）を1円に四捨五入（0 から遠い方。値引きは負）。DB の fn_referral_fee と同じ式 */
export function referralFeeOf(amount: number, pct: number | null | undefined): number {
  if (!pct || !amount) return 0;
  const a = BigInt(Math.round(Math.abs(amount)));
  const v = (a * BigInt(bpOf(pct)) + 5000n) / 10000n;
  return Number(amount < 0 ? -v : v);
}

/** 手数料の対象の行か：ふつうの行・率の行・値引きで、立替金でない */
export function isReferralTarget(l: { role: string; taxCategory?: string | null | undefined }): boolean {
  return (REFERRAL_ROLES as readonly string[]).includes(l.role) && !(l.taxCategory && taxKindOf(l.taxCategory) === "reimbursement");
}

/** 行の率：見積にパートナーが無ければ null。行の上書き（0＝対象外）か見積の率 */
export function lineReferralPct(l: { role: string; taxCategory?: string | null | undefined; referralPct?: number | null | undefined }, docPct: number | null | undefined): number | null {
  if (docPct == null || !isReferralTarget(l)) return null;
  return l.referralPct ?? docPct;
}

export type ReferralLineFee = { pct: number; fee: number; amount: number; custom: boolean };

/**
 * 手数料の集計（使う内訳の行だけを渡す＝linesInUse を通してから）。合計＝行の手数料の和。
 * amountOf は DB が計算した金額（画面は calc.amounts、API は EstimateLine.amount）
 */
export function referralFeesOf<L extends { id: string; role: string; taxCategory?: string | null | undefined; referralPct?: number | null | undefined }>(
  lines: readonly L[],
  docPct: number | null | undefined,
  amountOf: (l: L) => number,
): { total: number; base: number; byLine: Record<string, ReferralLineFee> } {
  const byLine: Record<string, ReferralLineFee> = {};
  let total = 0;
  let base = 0;
  if (docPct == null) return { total, base, byLine };
  for (const l of lines) {
    const pct = lineReferralPct(l, docPct);
    if (pct == null) continue;
    const amount = amountOf(l);
    const fee = referralFeeOf(amount, pct);
    byLine[l.id] = { pct, fee, amount, custom: l.referralPct != null };
    total += fee;
    base += amount;
  }
  return { total, base, byLine };
}

/**
 * 「手数料の分が入っていない行」（docs/40 §7。止めない・知らせるだけ）：仕入れがあるふつうの行で、行の率が 0 より大きく、
 * 手数料後の粗利率（1 − f − C ÷ S）が会社の粗利率の既定を下回る行（＝上乗せ ＜ 粗利率の既定＋パートナー の上乗せ）。
 * 定価の商品の据え置き（手数料は自社が持つ）も数える（正しい操作なので止めない）
 */
export function uncoveredLinesOf<L extends { id: string; role: string; taxCategory?: string | null | undefined; referralPct?: number | null | undefined; costAmount: number | null; costOwn?: boolean | undefined }>(
  lines: readonly L[],
  docPct: number | null | undefined,
  defaultMarginPct: number,
  amountOf: (l: L) => number,
): string[] {
  if (docPct == null) return [];
  const out: string[] = [];
  for (const l of lines) {
    if (l.role !== "detail" || l.costOwn || l.costAmount == null || l.costAmount <= 0) continue;
    const pct = lineReferralPct(l, docPct);
    if (!pct) continue;
    const amount = amountOf(l);
    const m = marginOfPrice(l.costAmount, amount, pct);
    if (m != null && pct2(m)! < defaultMarginPct) out.push(l.id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// 行の詳細の下段（docs/40 §5.2「どれを直すと何が変わるか」）。画面はこの関数で単価を決める（DB は 数量×単価 の金額だけ計算する）
//   粗利率 → 上乗せ → 単価／上乗せ（率でも円でも）→ 単価・粗利率／パートナー → 上乗せ → 単価（粗利率は変えない）／
//   単価・金額を直接 → 上乗せ・粗利率（ここは通らない）／仕入れ → 上乗せ・粗利率（単価は動かさない。単価が空なら ★B）
// ---------------------------------------------------------------------------

/** 行の売価（数量 × 単価。数量が空なら 1）。表示の粗利率・上乗せに使う（DB の金額の丸めの前の値） */
export function saleOf(l: { quantity: number | null | undefined; unitPrice: number | null | undefined }): number | null {
  if (l.unitPrice == null) return null;
  const q = l.quantity != null && l.quantity > 0 ? l.quantity : 1;
  return l.unitPrice * q;
}

export type LinePricingInput = { costAmount: number | null; quantity: number | null; unitPrice: number | null; referralPct?: number | null | undefined };
export type LinePricingEdit =
  | { kind: "margin"; value: number }
  | { kind: "markupPct"; value: number }
  | { kind: "markupYen"; value: number }
  /** パートナー（null＝見積の率に従う）。粗利率は変えずに上乗せ → 単価 */
  | { kind: "referral"; value: number | null };
export type LinePricingResult = { ok: true; unitPrice: number | null; referralPct: number | null } | { ok: false; error: "sumOver" | "noCost" };

/**
 * 行の詳細の 粗利率・上乗せ・パートナー を直したときの単価と行の率（docs/40 §5.2）。
 * 仕入れの無い行（自社・未定）は粗利率・上乗せを直せない（noCost）。パートナーは直せる（単価は動かさない）。
 * defaultMarginPct：単価が空の行でパートナーを直したときに使う粗利率（会社の既定）
 */
export function repriceLine(l: LinePricingInput, docPct: number | null | undefined, edit: LinePricingEdit, round: number, defaultMarginPct: number): LinePricingResult {
  const curRef = l.referralPct ?? docPct ?? 0;
  const cost = l.costAmount;
  if (edit.kind === "referral") {
    const nextRef = edit.value ?? docPct ?? 0;
    if (cost == null || cost <= 0) return { ok: true, unitPrice: l.unitPrice, referralPct: edit.value };
    const sale = saleOf(l);
    const m = sale != null ? pct2(marginOfPrice(cost, sale, curRef)) : defaultMarginPct;
    if (m == null) return { ok: true, unitPrice: l.unitPrice, referralPct: edit.value };
    if (m + nextRef >= 100) return { ok: false, error: "sumOver" };
    return { ok: true, unitPrice: unitPriceFromRate(cost, l.quantity, m + nextRef, round), referralPct: edit.value };
  }
  if (cost == null || cost <= 0) return { ok: false, error: "noCost" };
  if (edit.kind === "margin") {
    if (edit.value + curRef >= 100) return { ok: false, error: "sumOver" };
    return { ok: true, unitPrice: unitPriceFromRate(cost, l.quantity, edit.value + curRef, round), referralPct: l.referralPct ?? null };
  }
  if (edit.kind === "markupPct") return { ok: true, unitPrice: unitPriceFromMarkup(cost, l.quantity, edit.value, round), referralPct: l.referralPct ?? null };
  return { ok: true, unitPrice: unitPriceFromYen(cost, l.quantity, edit.value, round), referralPct: l.referralPct ?? null };
}

/** ★B（docs/40 §5.2）：仕入れを打って単価が空の行は、会社の既定の粗利率とパートナーで単価を入れる（単価がある行は null＝触らない） */
export function autoUnitPrice(l: LinePricingInput, docPct: number | null | undefined, defaultMarginPct: number, round: number): number | null {
  if (l.unitPrice != null || l.costAmount == null || l.costAmount <= 0) return null;
  const f = l.referralPct ?? docPct ?? 0;
  if (defaultMarginPct + f >= 100) return null;
  return unitPriceFromRate(l.costAmount, l.quantity, defaultMarginPct + f, round);
}

/**
 * 見積のパートナーを付けた・率を変えた（docs/40 §4）：見積の率に従う行（行の率が空）で仕入れと単価のあるふつうの行は、
 * 粗利率を変えずに新しい率を上乗せした単価にする（決定3「上乗せする」）。返すのは単価が変わる行だけ（id → 単価）。
 * 外すとき（newDocPct＝null）は呼ばない（単価はそのまま＝上乗せ済みの単価はただの単価として残る）
 */
export function repriceForDocPct<L extends LinePricingInput & { id: string; role: string; taxCategory?: string | null | undefined; costOwn?: boolean | undefined }>(
  lines: readonly L[],
  oldDocPct: number | null | undefined,
  newDocPct: number,
  round: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const l of lines) {
    if (l.role !== "detail" || l.referralPct != null || l.costOwn || !isReferralTarget(l)) continue;
    if (l.costAmount == null || l.costAmount <= 0 || l.unitPrice == null) continue;
    const sale = saleOf(l);
    const m = sale != null ? pct2(marginOfPrice(l.costAmount, sale, oldDocPct ?? 0)) : null;
    if (m == null || m + newDocPct >= 100) continue;
    const u = unitPriceFromRate(l.costAmount, l.quantity, m + newDocPct, round);
    if (u != null && u !== l.unitPrice) out[l.id] = u;
  }
  return out;
}

/** 見積のパートナー（GET /estimates/:id の referral）。社内用。お客様向けの紙・送信には出さない */
export const ReferralInfo = z.object({
  partnerId: z.string().uuid(),
  /** 正式名（固めるときの名前） */
  name: z.string(),
  /** 略称（画面の表示。displayNameOf） */
  short: z.string(),
  /** この見積の率（％） */
  pct: z.number(),
  /** 顧客の登録の率（％。見積で変えたかを見せる） */
  registeredPct: z.number(),
  /** 顧客の登録の備考（社内用） */
  memo: z.string(),
});
export type ReferralInfo = z.infer<typeof ReferralInfo>;

/** 手数料の集計（GET /estimates/:id の referralSummary）。出した回は発行の瞬間に固めた値（document_referral_fees） */
export const ReferralSummary = z.object({
  /** 手数料の合計（行の和） */
  fee: Yen,
  /** 手数料の対象の金額の和（税抜・値引き後） */
  base: Yen,
  /** 手数料後の利益＝税抜の小計 − 仕入れ − 手数料。仕入れ未入力があれば null（ルール7） */
  profitAfterFee: Yen.nullable(),
  /** 手数料後の利益率（％・小数1桁）。小計が 0 以下・仕入れ未入力なら null */
  marginAfterFee: z.number().nullable(),
  /** 手数料の分が入っていない行の数（知らせるだけ） */
  uncovered: z.number().int(),
  /** 出した回の固めた値か */
  frozen: z.boolean(),
});
export type ReferralSummary = z.infer<typeof ReferralSummary>;

/** パートナーの候補（GET /partners/referrers）。見積の詳細の「パートナーを選択」 */
export const ReferrerOption = z.object({
  id: z.string().uuid(),
  name: z.string(),
  short: z.string(),
  pct: z.number(),
  memo: z.string(),
});
export type ReferrerOption = z.infer<typeof ReferrerOption>;

/** 会社の上乗せの設定（GET /estimates/:id の markup）：行の詳細の粗利率・★B の自動の単価・取込の1枚 */
export const MarkupSettings = z.object({
  /** 粗利率の既定（tenants.vendor_margin_pct） */
  marginPct: z.number(),
  /** 上乗せ後の単価の丸め */
  round: MarkupRound,
});
export type MarkupSettings = z.infer<typeof MarkupSettings>;

/**
 * 画面の文（docs/10 §5.1.2：名詞形のボタン・業務の言葉・「〜人」と書かない・説明は「？」へ）。{referrer} {customer} {vendorQuote} は画面が t() で埋める
 */
export const REFERRAL_MSG = {
  // 設定 → 見積・請求
  section: "{referrer}手数料",
  feature: "{referrer}手数料",
  featureHelp: "{customer}の詳細で{referrer}を登録し、見積の詳細で付けると手数料を単価に上乗せします。オフにしても登録と発行済みの見積は残ります。",
  round: "上乗せ後の単価の丸め",
  roundHelp: "粗利率・{referrer}・上乗せ率から単価を出すときの切り上げの単位。{vendorQuote}の取込も同じ。端数処理とは別の設定です。",
  roundUnit: "{n}円",
  // 顧客の詳細・一覧
  partnerSwitch: "{referrer}",
  pct: "手数料",
  memo: "備考",
  memoHelp: "社内用（{customer}には出ません）。20字まで",
  pctRange: "手数料は 0 より大きく 90 未満で入力してください（小数2桁）",
  memoTooLong: "備考は20字までです",
  // 見積の詳細
  docSwitch: "{referrer}",
  docPick: "{referrer}選択",
  docPickTitle: "{referrer}の選択",
  docNone: "{referrer}の登録がありません。{customer}の詳細で登録します。",
  docPct: "手数料",
  docPctHelp: "この見積だけの率。{customer}の登録は変わりません。率を変えると、{referrer}の欄が空の行の単価に上乗せします。",
  docChange: "変更",
  notReferrer: "{referrer}として登録した{customer}を選択してください",
  // 行の詳細の下段
  byRate: "率で",
  byYen: "円で",
  margin: "粗利率",
  referral: "{referrer}",
  /** #215：「上乗せ」→「上乗せ率」（仕入れに対する％）。「円で」の欄は上乗せ額 */
  markup: "上乗せ率",
  markupYen: "上乗せ額",
  /** 率で／円で の読み上げの名前 */
  pricingBy: "単価の決め方",
  helpLabel: "粗利率と上乗せ率の説明",
  plus: "＋",
  eq: "＝",
  sumOver: "合計は 100% 未満",
  lineHelp: "単価＝仕入れ ÷（1 − 粗利率 − {referrer}）を{round}円単位で切り上げ。上乗せ率・単価を修正すると粗利率が変わり、{referrer}は変わりません。{referrer}を 0 にすると対象外。",
  lineHelpNoRef: "単価＝仕入れ ÷（1 − 粗利率）を{round}円単位で切り上げ。上乗せ率・単価を修正すると粗利率が変わります。",
  // 表（仕入元の欄の下段）・合計の帯・頭の帯
  feeMark: "P",
  totalFee: "手数料",
  profitAfter: "手数料後の利益",
  // 知らせ・発行の確認
  repriced: "{referrer}の手数料を {n}行の単価に上乗せしました",
  uncovered: "手数料の分が入っていない行 {n}",
  uncoveredHelp: "手数料後の粗利率が既定（{m}%）を下回る行。定価の商品を据え置いた行ならこのまま発行できます。",
} as const;
